(() => {
  'use strict';
  const money = (cents, currency) => new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(cents / 100);
  const controllers = new WeakMap();
  document.addEventListener('click', async (event) => {
    const restart = event.target.closest('[data-new-quote]');
    if (restart) {
      try { sessionStorage.removeItem('ess-quote-attempt'); } catch {}
      window.location.reload(); return;
    }
    const thumb = event.target.closest('[data-image-src]');
    if (thumb) {
      const product = thumb.closest('[data-product]');
      if (!product) return;
      const main = product.querySelector('[data-main-image]');
      const template = thumb.querySelector('[data-gallery-image]');
      if (main && template) main.replaceChildren(template.content.cloneNode(true));
      product.querySelectorAll('[data-image-src]').forEach(node => node.setAttribute('aria-pressed', String(node === thumb)));
      return;
    }
    const link = event.target.closest('[data-option-link]');
    if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    event.preventDefault();
    const product = link.closest('[data-product]');
    controllers.get(product)?.abort();
    const controller = new AbortController(); controllers.set(product, controller);
    const url = new URL(link.href);
    const displayUrl = url.toString();
    url.searchParams.set('section_id', product.dataset.sectionId);
    // Prevent adding the old variant while a different selection is loading.
    product.querySelector('[name="add"]').disabled = true;
    product.setAttribute('aria-busy', 'true');
    product.querySelector('[data-product-status]').textContent = 'Updating selection…';
    const entered = [...product.querySelectorAll('input[name="quantity"], [name^="properties["]')].map(el => [el.name, el.value]);
    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error('Selection could not be loaded.');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const next = html.querySelector('[data-product]');
      if (!next) throw new Error('Selection could not be loaded.');
      if (controller.signal.aborted || !product.isConnected) return;
      for (const [name, value] of entered) {
        const field = [...next.querySelectorAll('input,select,textarea')].find(el => el.name === name);
        if (field) field.value = value;
      }
      product.replaceWith(next);
      history.replaceState({}, '', displayUrl);
      const focus = [...next.querySelectorAll('[data-option-link]')].find(el => el.dataset.optionId === link.dataset.optionId && el.dataset.optionPosition === link.dataset.optionPosition);
      focus?.focus({ preventScroll: true });
      next.querySelector('[data-product-status]').textContent = next.dataset.variantId ? 'Selection updated.' : 'This combination is unavailable. Choose another option.';
      document.dispatchEvent(new CustomEvent('ess:product-updated', { detail: { section: next, variantId: next.dataset.variantId } }));
    } catch (error) {
      if (error.name === 'AbortError') return;
      // Native navigation remains a reliable fallback for a failed section request.
      window.location.assign(displayUrl);
    }
  });
  const quoteSummary = (cart, showPrices = false) => {
    const lines = ['QUOTE REQUEST'];
    if (showPrices) lines.push(`Currency: ${cart.currency}`);
    cart.items.forEach((item, index) => {
      lines.push('', `${index + 1}. ${item.product_title}`, `Variant: ${item.variant_title || 'Default'} | SKU: ${item.sku || '—'} | Variant ID: ${item.variant_id}`, `Quantity: ${item.quantity}` + (showPrices ? ` | Unit: ${money(item.final_price, cart.currency)} | Line: ${money(item.final_line_price, cart.currency)}` : ''));
      Object.entries(item.properties || {}).forEach(([key, value]) => { if (value && !key.startsWith('_')) lines.push(`${key}: ${value}`); });
      if (item.selling_plan_allocation) lines.push(`Selling plan: ${item.selling_plan_allocation.selling_plan.name}`);
      lines.push(`Product: ${location.origin}${item.url}`);
    });
    if (showPrices) lines.push('', `Subtotal: ${money(cart.total_price, cart.currency)}`);
    lines.push('', `Cart notes: ${cart.note || ''}`, showPrices ? 'Prices are reference values. Staff will confirm pricing, shipping, taxes and availability.' : 'Our team will review your equipment list and contact you with a quote.');
    return lines.join('\n');
  };
  const getCart = async (root) => {
    const response = await fetch(root.dataset.cartUrl + '.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!response.ok) throw new Error('We could not read your cart. Please try again.');
    return response.json();
  };
  const syncCart = async (root) => {
    const cartForm = root.querySelector('#EssCartForm');
    if (!cartForm.reportValidity()) throw new Error('Please check the quantities in your cart.');
    // Read the live cart so another tab's new lines are not lost or silently omitted.
    const current = await getCart(root);
    const keys = new Set(current.items.map(item => item.key));
    if (keys.size !== cartForm.querySelectorAll('[data-line-key]').length) throw new Error('Your cart changed in another tab. Refresh this page before requesting a quote.');
    const updates = {};
    for (const input of cartForm.querySelectorAll('[data-line-key]')) {
      if (!keys.has(input.dataset.lineKey)) throw new Error('Your cart changed in another tab. Refresh this page before requesting a quote.');
      const live = current.items.find(item => item.key === input.dataset.lineKey);
      if (live.quantity !== Number(input.defaultValue)) throw new Error('Your cart quantities changed in another tab. Refresh this page before requesting a quote.');
      updates[input.dataset.lineKey] = Number(input.value);
    }
    const response = await fetch(root.dataset.cartUrl + '/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ updates, note: cartForm.elements.note.value }) });
    const cart = await response.json();
    if (!response.ok || cart.errors) throw new Error('The cart could not be updated. Please review your quantities.');
    if (!cart.items?.length) throw new Error('Add at least one item before requesting a quote.');
    for (const input of cartForm.querySelectorAll('[data-line-key]')) { const live = cart.items.find(item => item.key === input.dataset.lineKey); if (live) input.defaultValue = String(live.quantity); }
    return cart;
  };
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!form.matches('#EssQuoteContact')) return;
    event.preventDefault();
    if (form.dataset.busy === 'true' || !form.reportValidity()) return;
    const root = form.closest('[data-cart]');
    const status = form.querySelector('[data-quote-status]');
    const button = form.querySelector('[data-quote-submit]');
    form.dataset.busy = 'true'; button.disabled = true;
    status.textContent = 'Saving your equipment list…';
    try {
      const cart = await syncCart(root);
      form.querySelector('[data-quote-body]').value = quoteSummary(cart, root.dataset.showPrices === 'true');
      const data = new FormData(form);
      const payload = {
        name: data.get('contact[name]'), email: data.get('contact[email]'), company: data.get('contact[Company or department]'), phone: data.get('contact[phone]'), destination: data.get('contact[Delivery destination]'), requirements: data.get('contact[Requirements]'), website: data.get('website') || '',
        currency: cart.currency, note: cart.note || '',
        items: cart.items.map(item => ({ variantId: String(item.variant_id), quantity: item.quantity, properties: item.properties || {}, sellingPlan: item.selling_plan_allocation?.selling_plan?.id || null }))
      };
      // Retain the same key after an uncertain response to prevent duplicate drafts.
      const fingerprint = Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify(payload))))).map(byte => byte.toString(16).padStart(2, '0')).join('');
      let saved;
      try { saved = JSON.parse(sessionStorage.getItem('ess-quote-attempt') || 'null'); } catch { /* Storage can be disabled. */ }
      if (!saved || saved.fingerprint !== fingerprint) saved = { fingerprint, key: crypto.randomUUID() };
      try { sessionStorage.setItem('ess-quote-attempt', JSON.stringify(saved)); } catch { /* This form still keeps the key in memory. */ }
      form.essAttempt = form.essAttempt?.fingerprint === fingerprint ? form.essAttempt : saved;
      status.textContent = 'Creating your quote request…';
      const response = await fetch(root.dataset.quoteEndpoint, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ ...payload, requestId: form.essAttempt.key }) });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.reference) throw new Error(result.error || 'Your request could not be confirmed. Please try again.');
      const success = document.createElement('div'); success.className = 'ess-success'; success.tabIndex = -1; success.setAttribute('role', 'status');
      const heading = document.createElement('h3'); heading.textContent = `Quote request ${result.reference} received.`;
      const message = document.createElement('p'); message.textContent = 'Your request is saved. Our team will review it and contact you with a quote. Your cart has been kept for reference.';
      const restart = document.createElement('button'); restart.type = 'button'; restart.className = 'ess-small-button'; restart.dataset.newQuote = ''; restart.textContent = 'Start another quote request';
      success.append(heading, message, restart); form.replaceChildren(success); success.focus();
      // Keep the successful key for a repeated identical cart; starting a new browser
      // session or changing the request creates a new request. No cart is cleared.
    } catch (error) {
      status.textContent = error.message || 'Something went wrong. Please try again.';
      status.classList.add('ess-error'); button.disabled = false; form.dataset.busy = 'false';
    }
  });
  const ready = () => {
    document.querySelector('[data-form-success]')?.focus();
    document.querySelectorAll('[data-cart]').forEach(root => {
      const form = root.querySelector('#EssQuoteContact');
      if (!form || root.dataset.quoteMode !== 'tracked') return;
      form.dataset.tracked = 'true';
      form.querySelector('[data-quote-submit]').disabled = false;
    });
  };
  ready();
  document.addEventListener('shopify:section:load', ready);
})();
// Shopify country options include the matching province list as JSON.
document.querySelectorAll('[data-address-country]').forEach(country => {
  const form = country.closest('form'), province = form.querySelector('[data-address-province]');
  if (country.dataset.default) country.value = country.dataset.default;
  const update = () => {
    const options = JSON.parse(country.selectedOptions[0]?.dataset.provinces || '[]');
    province.replaceChildren(...options.map(([value, label]) => new Option(label, value)));
    form.querySelector('[data-province-wrapper]').hidden = options.length === 0;
    if (province.dataset.default) province.value = province.dataset.default;
  };
  country.addEventListener('change', update); update();
});
